import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import type { RefObject } from 'react'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, CellValueChangedEvent, ColumnState } from 'ag-grid-community'
import { CheckboxSetFilter } from './CheckboxSetFilter'
import { TextareaEditor } from './TextareaEditor'
import { ROW_HEIGHTS, type RowSize, type TableRow } from './types'
import { createEmptyRow, rowStore } from './tableData'
import { useAuthStore } from '../auth/authStore'
import { useEditingEngine } from './useEditingEngine'
import { useTableSelection } from './useTableSelection'
import { useTableClipboard } from './useTableClipboard'

export function useTableGrid(
  gridRef: RefObject<AgGridReact<TableRow>>,
  containerRef: RefObject<HTMLDivElement>,
  initialData?: TableRow[],
  procSlotUserMap?: Map<string, string>,
) {
  const userId = useAuthStore((s) => s.user?.id ?? 'guest')
  const colStateKey = `crm_table_col_state_${userId}`
  const { saveCell, saveField, toasts } = useEditingEngine(gridRef, procSlotUserMap)

  // ── Данные ────────────────────────────────────────────────────────────────
  const [rowData, setRowData] = useState<TableRow[]>(() => initialData ?? rowStore.current)
  const [rowSize, setRowSize] = useState<RowSize>('normal')

  const prevInitialRef = useRef<TableRow[] | undefined>(undefined)
  useEffect(() => {
    if (initialData && initialData !== prevInitialRef.current) {
      prevInitialRef.current = initialData
      setRowData(initialData)
      rowStore.current = initialData
    }
  }, [initialData])

  useEffect(() => {
    rowStore.current = rowData
  }, [rowData])

  // ── Выделение ячеек ───────────────────────────────────────────────────────
  const onFocusAtEnd = useCallback((rowIndex: number, totalRows: number) => {
    if (rowIndex === totalRows - 1) {
      setRowData((prev) => {
        if (rowIndex === prev.length - 1) return [...prev, createEmptyRow()]
        return prev
      })
    }
  }, [])

  const {
    selRef,
    extraCellsRef,
    getColIdx,
    getCellStyle,
    onCellMouseDown,
    onCellMouseOver,
    onCellFocused,
  } = useTableSelection(gridRef, containerRef, onFocusAtEnd)

  // ── Буфер обмена ──────────────────────────────────────────────────────────
  useTableClipboard({
    gridRef,
    containerRef,
    selRef,
    extraCellsRef,
    getColIdx,
    setRowData,
    saveField,
  })

  // ── Колонки ───────────────────────────────────────────────────────────────
  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      editable: true,
      sortable: true,
      filter: CheckboxSetFilter,
      floatingFilter: false,
      wrapText: rowSize === 'tall',
      autoHeight: rowSize === 'tall',
      wrapHeaderText: true,
      autoHeaderHeight: true,
      minWidth: 60,
      cellStyle: getCellStyle,
      cellEditor: TextareaEditor,
      suppressKeyboardEvent: (params) => {
        if (!params.editing) return false
        return ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(params.event.key)
      },
    }),
    [getCellStyle, rowSize],
  )

  // ── Редактирование ────────────────────────────────────────────────────────
  const onCellValueChanged = useCallback(
    (e: CellValueChangedEvent<TableRow>) => {
      if (!e.colDef.field) return
      const field = e.colDef.field as keyof TableRow
      const rowId = e.data.id
      setRowData((prev) => {
        const idx = prev.findIndex((r) => r.id === rowId)
        if (idx === -1) return prev
        const next = [...prev]
        const updated = { ...next[idx]!, [field]: e.newValue } as TableRow
        if (field === 'partNumber' && e.newValue && !e.data.date) {
          updated.date = new Date().toISOString().split('T')[0]!
        }
        next[idx] = updated
        rowStore.current = next
        return next
      })
      void saveCell(e)
    },
    [saveCell],
  )

  const addRow = useCallback(() => {
    setRowData((prev) => [...prev, createEmptyRow()])
  }, [])

  const processDataFromClipboard = useCallback((_params: unknown): string[][] | null => null, [])

  // ── Состояние колонок per-user ─────────────────────────────────────────────
  const saveColState = useCallback(() => {
    const state = gridRef.current?.api.getColumnState()
    if (!state) return
    localStorage.setItem(colStateKey, JSON.stringify(state))
  }, [colStateKey, gridRef])

  const onGridReady = useCallback(() => {
    const saved = localStorage.getItem(colStateKey)
    if (!saved) return
    try {
      const state = JSON.parse(saved) as ColumnState[]
      gridRef.current?.api.applyColumnState({ state, applyOrder: true })
    } catch {
      // некорректный JSON — игнорируем
    }
  }, [colStateKey, gridRef])

  const resetColState = useCallback(() => {
    localStorage.removeItem(colStateKey)
    gridRef.current?.api.resetColumnState()
    gridRef.current?.api.setFilterModel(null)
  }, [colStateKey, gridRef])

  return {
    rowData,
    rowSize,
    setRowSize,
    defaultColDef,
    ROW_HEIGHTS,
    onCellFocused,
    onCellMouseDown,
    onCellMouseOver,
    onCellValueChanged,
    addRow,
    processDataFromClipboard,
    onGridReady,
    saveColState,
    resetColState,
    toasts,
  }
}
